// Browser mirror of Smarticks::WhatsappTemplates::Rules
// (custom/app/services/smarticks/whatsapp_templates/rules.rb). Both run the shared case table
// spec/custom/fixtures/whatsapp_templates/rules_cases.json — change both together.
// Each validator returns null when the input passes, or [code, detail] when it fails.
// Lengths are counted in codepoints ([...s].length), like Ruby's String#length.
// No CSS class strings in this file: Tailwind does not scan it.

export const NAME_MAX = 512;
export const BODY_MAX = 1024;
export const EXAMPLE_MAX = 200;
export const CATEGORIES = ['UTILITY', 'MARKETING'];
// 1msg TemplateLanguageProp enum (OpenAPI v2.39, 65 codes).
export const LANGUAGES = [
  'af',
  'sq',
  'ar',
  'az',
  'bn',
  'bg',
  'ca',
  'zh_CN',
  'zh_HK',
  'zh_TW',
  'hr',
  'cs',
  'da',
  'nl',
  'en',
  'en_GB',
  'en_US',
  'et',
  'fil',
  'fi',
  'fr',
  'de',
  'el',
  'gu',
  'he',
  'hi',
  'hu',
  'id',
  'ga',
  'it',
  'ja',
  'kn',
  'kk',
  'ko',
  'lo',
  'lv',
  'lt',
  'mk',
  'ms',
  'mr',
  'nb',
  'fa',
  'pl',
  'pt_BR',
  'pt_PT',
  'pa',
  'ro',
  'ru',
  'sr',
  'sk',
  'sl',
  'es',
  'es_AR',
  'es_ES',
  'es_MX',
  'sw',
  'sv',
  'ta',
  'te',
  'th',
  'tr',
  'uk',
  'ur',
  'uz',
  'vi',
];

const VARIABLE = /\{\{(\d+)\}\}/g;
const NAME_FORMAT = /^[a-z0-9_]+$/;
// eslint-disable-next-line no-control-regex
const BODY_FORBIDDEN = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/;
// eslint-disable-next-line no-control-regex
const EXAMPLE_FORBIDDEN = /[\x00-\x1F\x7F]/;

export const codepoints = value => [...(value || '')].length;

export const variableToken = n => `{{${n}}}`;

// Variable numbers as strings, in first-appearance order, without repeats.
export const variableTokens = text => {
  const seen = [];
  [...(text || '').matchAll(VARIABLE)].forEach(match => {
    if (!seen.includes(match[1])) seen.push(match[1]);
  });
  return seen;
};

export const validateName = name => {
  if (typeof name !== 'string' || !NAME_FORMAT.test(name)) {
    return ['invalid_name', null];
  }
  if (name.length > NAME_MAX) return ['name_too_long', NAME_MAX];
  return null;
};

export const validateLanguage = language =>
  LANGUAGES.includes(language) ? null : ['invalid_language', null];

export const validateCategory = category =>
  CATEGORIES.includes(category) ? null : ['invalid_category', null];

export const validatePlaceholders = text => {
  const value = text || '';
  const triple = value.match(/\{{3,}|\}{3,}/);
  if (triple) return ['invalid_placeholders', triple[0]];

  const leftover = value.replace(VARIABLE, '');
  if (leftover.includes('{{') || leftover.includes('}}')) {
    const found = leftover.match(/\{\{[^}]*\}\}|\{\{|\}\}/);
    return ['invalid_placeholders', found ? found[0] : null];
  }

  const tokens = variableTokens(value);
  const ordered = tokens.every((token, index) => token === String(index + 1));
  return ordered ? null : ['variables_out_of_order', null];
};

export const validateBody = text => {
  if (typeof text !== 'string' || text.trim() === '') {
    return ['body_required', null];
  }
  if (codepoints(text) > BODY_MAX) return ['body_too_long', BODY_MAX];
  if (BODY_FORBIDDEN.test(text)) return ['invalid_body', null];

  const placeholderError = validatePlaceholders(text);
  if (placeholderError) return placeholderError;

  const trimmed = text.trim();
  if (/^\{\{\d+\}\}/.test(trimmed) || /\{\{\d+\}\}$/.test(trimmed)) {
    return ['dangling_variable', null];
  }
  const all = [...text.matchAll(VARIABLE)].length;
  if (all !== variableTokens(text).length) return ['repeated_variable', null];
  return null;
};

export const validateExamples = (examples, count) => {
  if (count === 0) return null;
  if (
    !Array.isArray(examples) ||
    examples.length !== count ||
    !examples.every(example => typeof example === 'string')
  ) {
    return ['missing_examples', null];
  }
  for (let index = 0; index < examples.length; index += 1) {
    const value = examples[index].trim();
    if (value === '') return ['missing_examples', index + 1];
    if (codepoints(value) > EXAMPLE_MAX)
      return ['example_too_long', EXAMPLE_MAX];
    if (
      EXAMPLE_FORBIDDEN.test(value) ||
      value.includes('{{') ||
      value.includes('}}')
    ) {
      return ['example_invalid', index + 1];
    }
  }
  return null;
};

// ---- header, footer, buttons (mirror of the Ruby rules) ----

export const HEADER_FORMATS = ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'];
export const HEADER_TEXT_MAX = 60;
export const MEDIA_EXTENSIONS = {
  IMAGE: ['jpg', 'jpeg', 'png'],
  VIDEO: ['mp4', '3gp'],
  DOCUMENT: ['pdf'],
};
export const FOOTER_MAX = 60;
export const BUTTONS_MAX = 10;
export const BUTTON_CAPS = {
  QUICK_REPLY: 10,
  URL: 2,
  PHONE_NUMBER: 1,
  COPY_CODE: 1,
};
export const BUTTON_TEXT_MAX = 25;
export const URL_MAX = 2000;
export const COPY_CODE_MAX = 15;
const MARKDOWN = /[*_~`]/;
const URL_ASCII = /^[\x21-\x7E]+$/;

const isBlank = value => typeof value !== 'string' || value.trim() === '';

export const validateHeader = header => {
  if (header === null || header === undefined) return null;
  if (typeof header !== 'object') return ['invalid_header', null];
  const format = String(header.format || '');
  if (!HEADER_FORMATS.includes(format)) return ['invalid_header', null];
  if (format === 'NONE') return null;

  if (format === 'TEXT') {
    const { text } = header;
    if (isBlank(text)) return ['header_text_required', null];
    if (codepoints(text) > HEADER_TEXT_MAX) {
      return ['header_text_too_long', HEADER_TEXT_MAX];
    }
    if (EXAMPLE_FORBIDDEN.test(text)) return ['invalid_header', null];
    if (MARKDOWN.test(text)) return ['header_formatting', null];
    const placeholderError = validatePlaceholders(text);
    if (placeholderError) return placeholderError;
    const tokens = variableTokens(text);
    if (tokens.length > 1 || [...text.matchAll(VARIABLE)].length > 1) {
      return ['header_too_many_variables', null];
    }
    if (tokens.length === 0) return null;
    const exampleError = validateExamples(
      header.example === undefined || header.example === null
        ? []
        : [header.example],
      1
    );
    return exampleError ? ['header_example_missing', null] : null;
  }

  const url = header.sample_url;
  if (typeof url !== 'string' || !url.startsWith('https://')) {
    return ['header_sample_missing', null];
  }
  let path = '';
  try {
    path = new URL(url).pathname;
  } catch (e) {
    return ['header_sample_missing', null];
  }
  const extension = path.split('.').pop().toLowerCase();
  return MEDIA_EXTENSIONS[format].includes(extension)
    ? null
    : ['header_sample_type', null];
};

export const validateFooter = text => {
  if (text === null || text === undefined || text === '') return null;
  if (typeof text !== 'string') return ['invalid_footer', null];
  if (codepoints(text) > FOOTER_MAX) return ['footer_too_long', FOOTER_MAX];
  if (text.includes('{{') || text.includes('}}')) {
    return ['footer_variable', null];
  }
  if (EXAMPLE_FORBIDDEN.test(text)) return ['invalid_footer', null];
  return null;
};

export const validateButtonLabel = text => {
  if (isBlank(text)) return ['button_label_required', null];
  if (codepoints(text.trim()) > BUTTON_TEXT_MAX) {
    return ['button_label_too_long', BUTTON_TEXT_MAX];
  }
  if (EXAMPLE_FORBIDDEN.test(text) || text.includes('{{')) {
    return ['button_label_invalid', null];
  }
  return null;
};

const validateUrlExample = (prefix, example) => {
  if (isBlank(example)) return ['url_example_missing', null];
  if (!example.startsWith(prefix) || example.length <= prefix.length) {
    return ['url_example_mismatch', prefix];
  }
  if (!URL_ASCII.test(example)) return ['url_not_latin', null];
  return example.length > URL_MAX ? ['url_too_long', URL_MAX] : null;
};

export const validateUrlButton = (url, example) => {
  if (isBlank(url)) return ['url_required', null];
  if (!url.startsWith('https://')) return ['url_not_https', null];
  if (url.length > URL_MAX) return ['url_too_long', URL_MAX];
  if (!URL_ASCII.test(url)) return ['url_not_latin', null];
  const variables = url.match(/\{\{[^}]*\}\}/g) || [];
  if (variables.length === 0 && !url.includes('{{') && !url.includes('}}')) {
    return null;
  }
  if (variables.length > 1) return ['url_too_many_variables', null];
  if (!url.endsWith('{{1}}') || variables[0] !== '{{1}}') {
    return ['url_variable_position', null];
  }
  return validateUrlExample(url.slice(0, -'{{1}}'.length), example);
};

// Loose browser check; the server normalises with Smarticks::Voice::PhoneNormalizer.
export const validatePhoneButton = phone => {
  const digits = String(phone || '').replace(/[\s\-()]/g, '');
  return /^\+?\d{7,15}$/.test(digits) ? null : ['phone_invalid', null];
};

export const validateCopyCode = (example, category) => {
  if (category !== 'MARKETING') return ['copy_code_marketing_only', null];
  if (isBlank(example)) return ['copy_code_example_missing', null];
  const value = example.trim();
  if (codepoints(value) > COPY_CODE_MAX) {
    return ['copy_code_too_long', COPY_CODE_MAX];
  }
  return /^[A-Za-z0-9\-_]+$/.test(value) ? null : ['copy_code_invalid', null];
};

const validateAction = (action, category) => {
  switch (action.type) {
    case 'URL':
      return (
        validateButtonLabel(action.text) ||
        validateUrlButton(action.url, action.example)
      );
    case 'PHONE_NUMBER':
      return (
        validateButtonLabel(action.text) ||
        validatePhoneButton(action.phone_number)
      );
    case 'COPY_CODE':
      return validateCopyCode(action.example, category);
    default:
      return ['invalid_buttons', null];
  }
};

export const validateButtons = (buttons, category) => {
  if (buttons === null || buttons === undefined) return null;
  const valid =
    typeof buttons === 'object' &&
    ['qr_first', 'qr_last'].includes(buttons.order) &&
    Array.isArray(buttons.quick_replies) &&
    Array.isArray(buttons.actions);
  if (!valid) return ['invalid_buttons', null];

  const types = [
    ...buttons.quick_replies.map(() => 'QUICK_REPLY'),
    ...buttons.actions.map(action => String(action.type)),
  ];
  if (types.length > BUTTONS_MAX) return ['buttons_too_many', BUTTONS_MAX];
  if (!types.every(type => BUTTON_CAPS[type])) return ['invalid_buttons', null];
  const capError = Object.entries(BUTTON_CAPS).find(
    ([type, cap]) => types.filter(t => t === type).length > cap
  );
  if (capError) return [`${capError[0].toLowerCase()}_too_many`, capError[1]];

  const replyError = buttons.quick_replies
    .map(reply => validateButtonLabel(reply.text))
    .find(Boolean);
  if (replyError) return replyError;
  return (
    buttons.actions
      .map(action => validateAction(action, category))
      .find(Boolean) || null
  );
};

// Warnings never block. Meta publishes no number for the variable-to-text ratio; a short body with a
// variable was rejected live on 2026-10-03 (spike S1), so warn below ~3 words per variable + 3.
export const bodyWarnings = text => {
  const warnings = [];
  const count = variableTokens(text).length;
  const words = (text || '')
    .replace(VARIABLE, ' ')
    .split(/\s+/)
    .filter(Boolean).length;
  if (count > 0 && words < count * 3 + 3)
    warnings.push(['variable_ratio', null]);
  if (/ {4,}/.test(text || '')) warnings.push(['consecutive_spaces', null]);
  return warnings;
};

// Send-side fillability (mirror of Rules.body_only_fillable?): true when every send-time value of a
// synced template is a body parameter. Automations fill body parameters only.
const NON_BODY_BUTTONS = ['COPY_CODE', 'OTP', 'FLOW', 'CATALOG', 'MPM', 'SPM'];
const NON_BODY_COMPONENTS = ['CAROUSEL', 'LIMITED_TIME_OFFER'];
export const bodyOnlyFillable = template =>
  !(template?.components || []).some(component => {
    const type = String(component.type || '').toUpperCase();
    if (NON_BODY_COMPONENTS.includes(type)) return true;
    if (type === 'HEADER') {
      const format = String(component.format || '').toUpperCase();
      return format === 'TEXT'
        ? String(component.text || '').includes('{{')
        : !!format;
    }
    if (type === 'BUTTONS') {
      return (component.buttons || []).some(button => {
        const buttonType = String(button.type || '').toUpperCase();
        return (
          NON_BODY_BUTTONS.includes(buttonType) ||
          (buttonType === 'URL' && String(button.url || '').includes('{{'))
        );
      });
    }
    return false;
  });
